import { useId, useState } from 'react';
import type { ExecutivePeriodKind, ExecutiveQuery } from '../../dashboard-v2/types';
import {
  MAX_CUSTOM_MONTHS, PERIOD_OPTIONS, dayToMonth, monthToFirstDay, monthToLastDay, monthsBetween,
} from '../periodQuery';

/**
 * The period choice for the executive dashboard. A custom range is chosen by whole months, so the headline, the comparison and the monthly chart always
 * cover the same months. The choice lives in the page address (the caller owns that), so a link, a reload and the browser's back and forward buttons all
 * give the same view. The other filters the brief asks for (department, location, work state, employee type, pay schedule) are not offered, and the page
 * says why instead of showing controls that would do nothing.
 */
export function PeriodControls({ value, onChange }: { value: ExecutiveQuery; onChange: (next: ExecutiveQuery) => void }) {
  const selectId = useId();
  const fromId = useId();
  const toId = useId();
  const [fromMonth, setFromMonth] = useState(dayToMonth(value.from));
  const [toMonth, setToMonth] = useState(dayToMonth(value.to));
  const [error, setError] = useState<string | null>(null);

  const applyCustom = () => {
    if (!fromMonth || !toMonth) {
      setError('Choose both a first and a last month.');
      return;
    }
    if (fromMonth > toMonth) {
      setError('The first month must not be after the last month.');
      return;
    }
    if (monthsBetween(fromMonth, toMonth) > MAX_CUSTOM_MONTHS) {
      setError(`A custom range can cover at most ${MAX_CUSTOM_MONTHS} months.`);
      return;
    }
    setError(null);
    onChange({ period: 'custom', from: monthToFirstDay(fromMonth), to: monthToLastDay(toMonth) });
  };

  return (
    <section className="ed-period-controls" aria-label="Reporting period" data-testid="period-controls">
      <div className="ed-period-controls__row">
        <label htmlFor={selectId}>Period</label>
        <select
          id={selectId}
          value={value.period}
          onChange={(e) => {
            const kind = e.target.value as ExecutivePeriodKind;
            setError(null);
            if (kind === 'custom') {
              // Do not ask the server for a custom range until both months are chosen; show the pickers first.
              if (value.from && value.to) onChange({ period: 'custom', from: value.from, to: value.to });
              else onChange({ period: 'custom' });
            } else {
              onChange({ period: kind });
            }
          }}
        >
          {PERIOD_OPTIONS.map((o) => <option key={o.kind} value={o.kind}>{o.label}</option>)}
        </select>

        {value.period === 'custom' && (
          <>
            <label htmlFor={fromId}>From</label>
            <input id={fromId} type="month" value={fromMonth} onChange={(e) => setFromMonth(e.target.value)} />
            <label htmlFor={toId}>To</label>
            <input id={toId} type="month" value={toMonth} onChange={(e) => setToMonth(e.target.value)} />
            <button type="button" className="ed-period-controls__apply" onClick={applyCustom}>Apply</button>
          </>
        )}
      </div>
      {error && <p role="alert" className="ed-period-controls__error">{error}</p>}
      <p className="ed-period-controls__note">
        Filters by department, location, work state, employee type and pay schedule are not available yet. See &quot;What this page cannot show yet&quot; below.
      </p>
    </section>
  );
}
